import { ActionList } from "./Actions";
import { LanguageOptions } from "../../Base/LanguageOptions";

export type LOBBY_TYPE = "solo" | "group" | "none";

export enum MINIGAME_TYPE {
  MULTI_CHOICE = "multi-choice",
  CODE = "code",
  LOCKPICK = "lockpick",
  TILE = "tile",
  MIC = "microphone",
  SHAKE = "shake",
  SWIPE_IMG = "swipe-image",
  MAP_RUNAWAY = "map_runaway",
  TAP = "tap-it",
  COLOUR_MATCH = "colour-match",
  IMAGE_MATCH = "img-match",
  NUMBER_JUMP = "number-jump",
  SYMBOL_TRAIN = "symbol-train",
  SWEET_SPOT = "sweet-spot",
  MARBLE_MAZE = "marble-maze",
  POI_PICKER = "poi-picker",
  INVENTORY_PICKER = "inventory-picker",
  POINT_TRACE = "point-trace",
  FLING_IT = "fling-it",
  LOOK_SEEK = "look-seek",
  ANAGRAM = "anagram",
  JIGSAW = "jigsaw",
  OVERLAYPUZZLE = "overlay-puzzle",
}

/**
 * @summary Base iface for ALL minigames
 */
interface BaseMiniGame {
  tutorial: {
    imgs: string;
    txt: LanguageOptions;
    title: LanguageOptions;
    audio: LanguageOptions;
  };
  timeout_s: number; // How long do players have to complete it. 0 = infinte
  styleID: number; // Identifier in case we want to use visual variants within one game
  dismissable: boolean; // allows for the closure without forfeit. Also skips lobby?
}

/**
 * @summary Base iface for On Device Minigames
 */
export interface MiniGame extends BaseMiniGame {
  lobbyType: LOBBY_TYPE; // Codebreaker & Multi-choice MUST = solo. LockPick & Tile = group
  attempts: number; // How many attempts allowed. 0 = infinte
  puzzleData: {
    default:
      | MultiChoicePuzzleData
      | CodePuzzleData
      | LockpickPuzzleData
      | TilePuzzleData
      | MicPuzzleData
      | ShakePuzzleData
      | SwipeImgPuzzleData
      | ColourMatchPuzzleData
      | MapRunawayPuzzleData
      | NumberJumpPuzzleData
      | ImageMatchPuzzleData
      | SweetSpotPuzzleData
      | SymbolTrainPuzzle
      | MarbleMazePuzzle
      | PoiPickerPuzzle
      | InventoryPickerPuzzle
      | PointTracePuzzleData
      | FlingItPuzzleData
      | LookSeekPuzzleData
      | AnagramPuzzleData
      | JigsawPuzzleData
      | OverlayPuzzleData;
    easy?:
      | MultiChoicePuzzleData
      | CodePuzzleData
      | LockpickPuzzleData
      | TilePuzzleData
      | MicPuzzleData
      | ShakePuzzleData
      | SwipeImgPuzzleData
      | ColourMatchPuzzleData
      | MapRunawayPuzzleData
      | NumberJumpPuzzleData
      | ImageMatchPuzzleData
      | SweetSpotPuzzleData
      | SymbolTrainPuzzle
      | MarbleMazePuzzle
      | PoiPickerPuzzle
      | InventoryPickerPuzzle
      | PointTracePuzzleData
      | FlingItPuzzleData
      | LookSeekPuzzleData
      | JigsawPuzzleData
      | AnagramPuzzleData
      | OverlayPuzzleData;
    hard?:
      | MultiChoicePuzzleData
      | CodePuzzleData
      | LockpickPuzzleData
      | TilePuzzleData
      | MicPuzzleData
      | ShakePuzzleData
      | SwipeImgPuzzleData
      | ColourMatchPuzzleData
      | MapRunawayPuzzleData
      | NumberJumpPuzzleData
      | ImageMatchPuzzleData
      | SweetSpotPuzzleData
      | SymbolTrainPuzzle
      | MarbleMazePuzzle
      | PoiPickerPuzzle
      | InventoryPickerPuzzle
      | PointTracePuzzleData
      | FlingItPuzzleData
      | LookSeekPuzzleData
      | JigsawPuzzleData
      | AnagramPuzzleData
      | OverlayPuzzleData;
  };
}

interface PuzzleData {
  onFail?: {
    txt?: LanguageOptions;
    actions?: ActionList;
  };

  onSuccess?: {
    txt?: LanguageOptions;
    actions?: ActionList;
  };

  onIncorrect?: {
    txt?: LanguageOptions;
    actions?: ActionList;
  };
}

/**
 * @summary Press the numbers in ascending order(group activity)
 */
export interface NumberJumpPuzzleData extends PuzzleData {
  numRounds: number; // How many rounds
  cells: number; // int how many numbers to show on screen each round,
  maxRange: number; // int (10-100) Maximim range of numbers from 0-range
}

/**
 * @summary Match the pair of images amongst the jumble (group activity)
 */
export interface ImageMatchPuzzleData extends PuzzleData {
  numRounds: number; // How many rounds
  cells: number; // int how many images to show on screen each round
}

/**
 * @summary Team must reach the target before the timer runs out (Leader activity - no lobby)
 */
export interface MapRunawayPuzzleData extends PuzzleData {
  targets: { [poiID: string]: { id: string; value: boolean } };
  timeout_Adjust_s: number; // Add this to timeOut_s to inc/decr the allowed duration
}

/**
 * @summary Select the correct option from 3 possible (Leader activity)
 */
export interface MultiChoicePuzzleData extends PuzzleData {
  q: {
    txt: LanguageOptions;
    imgs: string;
    audio?: LanguageOptions;
  };
  a: {
    a0: { correct: boolean; txt: LanguageOptions };
    a1: { correct: boolean; txt: LanguageOptions };
    a2: { correct: boolean; txt: LanguageOptions };
  };
}

/**
 * @summary Input the correct values. Keypad / Text input / other versions using styleID (Leader activity)
 */
export interface CodePuzzleData extends PuzzleData {
  q: { txt: LanguageOptions; audio?: LanguageOptions; imgs: string };
  a: { txt: LanguageOptions }; // acceptible answers delineated by *
}

/**
 * @summary Unpick the lock (Group activity)
 */
export interface LockpickPuzzleData extends PuzzleData {
  pins: number;
  speed: number;
  scale: number;
}

/**
 * @summary Complete a Tile Puzzle (Group activity)
 */
export interface TilePuzzleData extends PuzzleData {
  imgs?: string;
  divs: number;
  shuffles: number;
}

/**
 * @summary Shout into the mic to hit the threshold (group activity)
 */
export interface MicPuzzleData extends PuzzleData {
  bleedRate_Scalar: number; // Range 0-0.9. Affects how fast the slider drains out
}

/**
 * @summary Shake the phone to hit the threshold (group activity)
 */
export interface ShakePuzzleData extends PuzzleData {
  bleedRate_Scalar: number; // Range 0-0.9. Affects how fast the slider drains out
}

/**
 * @summary Swipe the screen to reveal the lower image (group activity)
 */
export interface SwipeImgPuzzleData extends PuzzleData {
  imgs: string[];
  completionPc: number; // defaults to 50
}

/**
 * @summary Swipe the screen to reveal the lower image (group activity)
 */
export interface TapItPuzzleData extends PuzzleData {
  bleedRate_Scalar: number; // Range 0-0.9. Affects how fast the slider drains out
}

/**
 * @summary Tap the button showing the displayed colour of the word (group activity)
 */
export interface ColourMatchPuzzleData extends PuzzleData {
  numRounds: number; // How many rounds
  sPerRound: number; // float how many secs per round
  colorIDs: LanguageOptions; // comma delineated string of colour names as per https://docs.microsoft.com/en-us/dotnet/api/system.drawing.color?view=net-6.0
}

/**
 * @summary Press the screen as close as possible to the given value. Within the range
 * defaults...
 * Title: Sweet Spot
 * Tutorial Body: Hit within the sweet spot!
 * timeout_s: 0 (Disable input)
 * attempts: 5
 */
export interface SweetSpotPuzzleData extends PuzzleData {
  target_val: number; // float (0-1) what value are they aiming for? default= 0.5
  range_pc: number; // float (0-1) how close do they need to get. 0.1 = 10%? default= 0.5
  speed: number; // float (0-1) speed scalar for the pip movement if needed. default= 0.5
}

/**
 * @summary Find the images/symbols from the conveyor that match your target
 * defaults...
 * Title: Symbol Train
 * Tutorial Body: Find the images/symbols that match your targets!
 * timeout_s: 60
 * attempts: 5
 */
export interface SymbolTrainPuzzle extends PuzzleData {
  num_targets: number; // int (1-6) how many targets to find? default= 3
  speed: number; // float (0-1) speed scalar for the conveyor movement speed. default= 0.5
}

/**
 * @summary Select the Guilty Character. Players are presented with a select list of POIs, they must select the correct ones. This is ENFORCED as dismissable. DEFAULT DIFFICULTY ONLY. >=1 target MUST be selected, max=3.
 * defaults...
 * key: poi-picker
 * Title: Whodunnit?
 * Tutorial Body: Select the culprit(s) from the list!
 * dismissable:  true, // this is locked to true
 * lobby type: solo
 * timeout_s: 0
 * attempts: 0
 */
export interface PoiPickerPuzzle extends PuzzleData {
  q: {
    txt: LanguageOptions;
    imgs?: string;
    audio?: LanguageOptions;
  };
  poi_targets: { [key: string]: boolean }; // list of poiIDs with true/false to indicate if they are the target. Each MUST be featured in the refs to block the targets being deleted once set.  Admin used must ADD them to the list and then mark them as targets.
}

/**
 * @summary Select the Target Inventory Item. Players are presented with a select list of Inventory Items and must select the correct ones. This is ENFORCED as dismissable. DEFAULT DIFFICULTY ONLY. >=1 target MUST be selected, max=3.
 * defaults...
 * key: inventory-picker
 * Title: Item Pick!
 * Tutorial Body: Select the item from the list!
 * dismissable:  true, // this is locked to true
 * lobby type: solo
 * timeout_s: 0
 * attempts: 0
 */
export interface InventoryPickerPuzzle extends PuzzleData {
  q: {
    txt: LanguageOptions;
    imgs?: string;
    audio?: LanguageOptions;
  };
  inv_targets: { [key: string]: boolean }; // list of InventoryIDs with true/false to indicate if they are the target. Each MUST be featured in the refs to block the targets being deleted once set. Admin used must ADD them to the list and then mark them as targets.
}

/**
 * @summary Defeat each maze. Players are presented with a number of rounds of a marble maze in which they must navigate the 'blue' ball to the exit. Each maze is randomised from a set dependent on difficulty.
 * defaults...
 * key: marble-maze
 * Title: Marble Maze
 * Tutorial Body: Navigate each maze!
 * dismissable:  false, // this is locked to false
 * lobby type: group
 * timeout_s: 120
 * attempts: 5
 */
export interface MarbleMazePuzzle extends PuzzleData {
  numRounds: number; // How many rounds (1-10)
  map: string; // pathTo pixel image indicating map. map is determined by BLACK, RED=hazard, Yellow=obstacle, MUST BE Po2 SQUARE
}
export interface PointTracePuzzleData extends PuzzleData {
  // Players must trace a line between a number of points as displayed on an image. This could be tracing a magical rune, a constellation or simply a letterform.
  bailOnHit: boolean; // Lose an attempt immediately when hitting a point not in the sequence?
  showOrderHints: boolean; // highlight the next point in the sequence?
  hitMap: string; // lowRes image indicating points. Order is defined by alpha on each point. sub-sections are defined by colour. MUST BE Po2 SQUARE
  imgBackdrop?: string; // Image to display UNDER the points. MUST BE Po2 SQUARE
}

/**
 * @summary Players must swipe/rotate to find the specified target images withn a grid of images ie find a treasureBox within a 'maze'
 * @note ! targetImgs are EITHER a centered target image to look for OR a cell with a background (note this requires knowledge of the style)
 * @defaults
 * key: look-seek
 * Title: Look & Seek
 * Tutorial Body: Find the targets!
 * dismissable:  false,
 * lobby type: group
 * timeout_s: 90
 * attempts: 0 // disabled
 */
export interface LookSeekPuzzleData extends PuzzleData {
  targetImgs: string[]; // Images from storage 512x512max. !!! validation: REQUIRED !!!
  gridDivs: number; // # Grid Divisions (slider(5-15) default 8)
}

/**
 * @summary Players swipe up to throw/fire projectiles at a target
 * @note !
 * @defaults
 * key: fling-it
 * Title: Fling it
 * Tutorial Body: Hit the target/s!
 * dismissable:  false,
 * lobby type: group
 * timeout_s: 90
 * attempts: 0
 */
export interface FlingItPuzzleData extends PuzzleData {
  ammoCount: number; // slider(1 - 1k) default=20 !!! validation: must be >= hitGoal !!
  hitGoal: number; // slider(1 - 100) default=10
}

/**
 * @summary Solve the Anagram
 */
export interface AnagramPuzzleData extends PuzzleData {
  q: { txt: LanguageOptions; audio?: LanguageOptions; imgs: string };
  a: { txt: LanguageOptions };
}

/**
 * @summary Re-arrange the pieces
 * @notes Players must rearrange the pieces within the bounded area. Styles determine wether the image is torn-paper, shattered or jigsaw puzzle shapes. Cell value suggests the # of cells (jigsaw style will round down to the nearest square number). helper Puts an initial piece in place as a helper.
 * defaults...
 * Title: Jigsaw
 * Tutorial Body: Rearrange the pieces!
 * timeout_s: 90
 */
export interface JigsawPuzzleData extends PuzzleData {
  imgs: string; // storage path to image to be split.
  cells: number; // how many pieces should the image be split into? (some styles may round up/down) min 3, max 20
  helper: boolean; // Should a random pice be placed by default? (assists with difficulty)
}

/**
 * @summary Select the appropriate pieces from the list to build the target image
 * @notes Players must select from a list of pieces to 'recreate' the target image. Num Rounds determines how many rounds are offered, use this paired with max Attempts to alter difficulty.
 * Title: Overlay Match
 * Tutorial Body: Select the correct layers to match the target image.
 * timeout_s: 90
 * dismissable:  false,
 * lobby type: group
 */
export interface OverlayPuzzleData extends PuzzleData {
  numRounds: number; // How many rounds (1-10)
}
